import { FC, useState } from 'react';
import { Box, Button, Divider, IconButton, Menu, Paper, Typography } from '@mui/material';
import { Tune } from '@mui/icons-material';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ColumnSelectorItem } from '@hooks/useColumnVisibility';
import ColumnItem from './ColumnItem';
import SortableColumnItem from './SortableColumnItem';

export type ColumnSelectorDropdownProps = {
  selectedItems: ColumnSelectorItem[];
  unselectedItems: ColumnSelectorItem[];
  onToggle: (key: string) => void;
  onReorder: (activeKey: string, overKey: string) => void;
  onReset: () => void;
};

const sectionHeaderSx = {
  fontSize: '0.875rem',
  fontWeight: 600,
  textAlign: 'center',
};

const ColumnSelectorDropdown: FC<ColumnSelectorDropdownProps> = ({
  selectedItems,
  unselectedItems,
  onToggle,
  onReorder,
  onReset,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const isOpen = Boolean(anchorEl);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require an 8px drag before activating to prevent checkbox clicks from triggering DnD.
      activationConstraint: { distance: 8 },
    }),
  );

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDragStart = (event: { active: { id: string | number } }) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (over && active.id !== over.id) {
      onReorder(String(active.id), String(over.id));
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const handleSelectAll = () => {
    unselectedItems.forEach((item) => onToggle(item.key));
  };

  const activeItem = activeId ? selectedItems.find((i) => i.key === activeId) : null;

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        aria-label="Select columns"
        aria-controls={isOpen ? 'column-selector-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : undefined}
        sx={{
          color: isOpen ? '#2F736E' : '#162936',
          padding: '4px',
          '&:hover': { color: '#2F736E' },
        }}
      >
        <Tune sx={{ fontSize: '1.25rem' }} />
      </IconButton>

      <Menu
        id="column-selector-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '0.5rem',
              width: '240px',
              boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
              p: 0,
            },
          },
        }}
        MenuListProps={{
          disablePadding: true,
          'aria-labelledby': 'column-selector-button',
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '0.75rem',
            pt: '0.75rem',
            pb: '0.25rem',
          }}
        >
          <Typography sx={sectionHeaderSx}>Selected Fields</Typography>
          <Button
            size="small"
            variant="text"
            onClick={onReset}
            sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              textTransform: 'none',
              color: '#2F736E',
              minWidth: 'auto',
            }}
          >
            Reset
          </Button>
        </Box>

        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={selectedItems.map((i) => i.key)}
            strategy={verticalListSortingStrategy}
          >
            <Box>
              {selectedItems.map((item) => (
                <SortableColumnItem
                  key={item.key}
                  id={item.key}
                  label={item.label}
                  checked
                  disabled={item.compulsory}
                  onToggle={onToggle}
                />
              ))}
            </Box>
          </SortableContext>

          {/* DragOverlay renders the ghost in a portal above MUI's Menu portal,
              preventing clipping and keeping the drag visual consistent. */}
          <DragOverlay>
            {activeItem ? (
              <Paper
                elevation={4}
                sx={{ borderRadius: '0.5rem', bgcolor: 'background.paper', width: '240px' }}
              >
                <ColumnItem
                  id={activeItem.key}
                  label={activeItem.label}
                  checked
                  disabled={activeItem.compulsory}
                  onToggle={() => {}}
                  dragHandleProps={{}}
                />
              </Paper>
            ) : null}
          </DragOverlay>
        </DndContext>

        {unselectedItems.length > 0 && (
          <>
            <Divider sx={{ my: '0.5rem', borderColor: '#E8EAEB' }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: '0.75rem',
                pt: '0.75rem',
                pb: '0.25rem',
              }}
            >
              <Typography sx={sectionHeaderSx}>Unselected</Typography>
              <Button
                size="small"
                variant="text"
                onClick={handleSelectAll}
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  color: '#2F736E',
                  minWidth: 'auto',
                }}
              >
                Select All
              </Button>
            </Box>
            <Box pb="0.5rem">
              {unselectedItems.map((item) => (
                <ColumnItem
                  key={item.key}
                  id={item.key}
                  label={item.label}
                  checked={false}
                  onToggle={onToggle}
                />
              ))}
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default ColumnSelectorDropdown;
